import { defineStore } from 'pinia'

export type ViewAsRole = 'Client' | 'Advisor' | 'Representative'

/**
 * "View as" state — an admin (or advisor, limited to Client) previewing the
 * portal as a different role / household. This is a DISPLAY override only: it
 * changes which chrome + feature gates render. It NEVER changes the caller's
 * real token, so the backend still authorizes by their real role (an admin
 * already has access to every household, so previewing a lower role can only
 * HIDE, never grant). See useViewAs() for the consuming logic.
 *
 * Persisted in sessionStorage (per-tab) so a reload keeps the preview, exactly
 * like the household/tab selection.
 */
export const useViewAsStore = defineStore('view-as', {
  state: () => ({
    role: null as ViewAsRole | null,
    householdId: null as number | null,    // set only when role === 'Client'
    householdName: null as string | null,  // for the banner label
    repId: null as number | null,          // set only when role === 'Representative'
    repName: null as string | null         // for the banner label
  }),
  getters: {
    active: s => s.role !== null
  },
  actions: {
    set(
      role: ViewAsRole,
      opts?: { householdId?: number | null, householdName?: string | null, repId?: number | null, repName?: string | null }
    ) {
      this.role = role
      this.householdId = role === 'Client' ? (opts?.householdId ?? null) : null
      this.householdName = role === 'Client' ? (opts?.householdName ?? null) : null
      this.repId = role === 'Representative' ? (opts?.repId ?? null) : null
      this.repName = role === 'Representative' ? (opts?.repName ?? null) : null
    },
    clear() {
      this.role = null
      this.householdId = null
      this.householdName = null
      this.repId = null
      this.repName = null
    }
  },
  persist: {
    storage: piniaPluginPersistedstate.sessionStorage()
  }
})
